// Project Detail — Sites & Buildings panel.
// Project → multiple Sites → each Site has multiple Buildings/addresses.
// Allocate existing client sites, create new sites, and add buildings under each site.
import { useCallback, useEffect, useState } from "react";
import { MapPin, Building as BuildingIcon, Plus, Loader2, Link2, X, Save, Unlink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SectionCard } from "@/components/SectionCard";

export default function ProjectSitesPanel({ project, canEdit, onChanged }) {
    const [sites, setSites] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allocating, setAllocating] = useState(false);
    const [allocId, setAllocId] = useState("");
    const [newSite, setNewSite] = useState(null);     // {name, address} | null
    const [newBldg, setNewBldg] = useState(null);      // {siteId, name, ...} | null
    const [busy, setBusy] = useState(false);

    const siteIds = project.site_ids || (project.site_id ? [project.site_id] : []);
    const buildingIds = project.building_ids || [];

    const refresh = useCallback(async () => {
        try {
            const [s, b] = await Promise.all([
                api.get("/sites", { params: { client_id: project.client_id } }),
                api.get("/buildings", { params: { client_id: project.client_id } }),
            ]);
            setSites(s.data); setBuildings(b.data);
        } catch { /* noop */ }
        setLoading(false);
    }, [project.client_id]);

    useEffect(() => { refresh(); }, [refresh]);

    const projectSites = sites.filter((s) => siteIds.includes(s.id));
    const unlinkedSites = sites.filter((s) => !siteIds.includes(s.id));

    async function allocate() {
        if (!allocId) return;
        setBusy(true);
        try {
            await api.patch(`/projects/${project.id}`, { site_ids: [...siteIds, allocId] });
            toast.success("Site linked to project");
            setAllocating(false); setAllocId("");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not link site"); }
        finally { setBusy(false); }
    }

    async function unlink(siteId) {
        if (!window.confirm("Remove this site from the project? (The site itself is not deleted.)")) return;
        try {
            const remainingBuildings = buildingIds.filter((bid) => {
                const b = buildings.find((x) => x.id === bid);
                return !b || b.site_id !== siteId;
            });
            await api.patch(`/projects/${project.id}`, { site_ids: siteIds.filter((x) => x !== siteId), building_ids: remainingBuildings });
            toast.success("Site removed from project");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not remove site"); }
    }

    async function createSite() {
        if (!newSite.name.trim()) { toast.error("Site name is required"); return; }
        setBusy(true);
        try {
            const { data: site } = await api.post("/sites", {
                client_id: project.client_id, name: newSite.name.trim(), address: newSite.address?.trim() || undefined,
            });
            await api.patch(`/projects/${project.id}`, { site_ids: [...siteIds, site.id] });
            toast.success(`Site created & linked: ${site.name}`);
            setNewSite(null);
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create site"); }
        finally { setBusy(false); }
    }

    async function createBuilding() {
        if (!newBldg.name.trim()) { toast.error("Building name is required"); return; }
        setBusy(true);
        try {
            const { data: bldg } = await api.post("/buildings", {
                client_id: project.client_id, site_id: newBldg.siteId, name: newBldg.name.trim(),
                building_type: newBldg.building_type || undefined,
            });
            await api.patch(`/projects/${project.id}`, { building_ids: [...buildingIds, bldg.id] });
            toast.success(`Building added: ${bldg.name}`);
            setNewBldg(null);
            onChanged?.();
            await refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add building"); }
        finally { setBusy(false); }
    }

    return (
        <SectionCard testId="project-sites-panel" id={`sites-${project.id}`} icon={MapPin}
            title="Sites & Buildings" count={`${projectSites.length} site(s)`}
            actions={canEdit && (
                    <div className="flex items-center gap-2">
                        <button data-testid="psp-allocate-toggle" onClick={() => { setAllocating((v) => !v); setNewSite(null); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[8px] text-[11.5px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                            <Link2 className="w-3.5 h-3.5" /> Link existing site
                        </button>
                        <button data-testid="psp-new-site" onClick={() => { setNewSite({ name: "", address: "" }); setAllocating(false); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            <Plus className="w-3.5 h-3.5" /> New site
                        </button>
                    </div>
            )}>

            {allocating && (
                <div className="flex items-center gap-2 mb-3 rounded-lg border p-2.5" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                    <select data-testid="psp-allocate-select" value={allocId} onChange={(e) => setAllocId(e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }}>
                        <option value="">Select a site to link…</option>
                        {unlinkedSites.map((s) => <option key={s.id} value={s.id}>{s.name}{s.address ? ` — ${s.address}` : ""}</option>)}
                    </select>
                    <button data-testid="psp-allocate-save" onClick={allocate} disabled={busy || !allocId}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Link
                    </button>
                </div>
            )}

            {newSite && (
                <div className="mb-3 rounded-lg border p-3 space-y-2" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }} data-testid="psp-new-site-form">
                    <input data-testid="psp-new-site-name" value={newSite.name} onChange={(e) => setNewSite((p) => ({ ...p, name: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} placeholder="Site name" />
                    <input data-testid="psp-new-site-address" value={newSite.address} onChange={(e) => setNewSite((p) => ({ ...p, address: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} placeholder="Address (optional)" />
                    <div className="flex gap-2">
                        <button data-testid="psp-new-site-save" onClick={createSite} disabled={busy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Create & link
                        </button>
                        <button onClick={() => setNewSite(null)} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="py-6 text-center text-[12px]" style={{ color: "#9A968F" }}><Loader2 className="inline animate-spin mr-1.5" size={13} />Loading…</div>
            ) : projectSites.length === 0 ? (
                <div className="py-6 text-center text-[12.5px] italic" style={{ color: "#9A968F" }}>
                    No sites linked to this project yet. {canEdit ? "Link an existing site or create a new one above." : ""}
                </div>
            ) : (
                <div className="space-y-2.5">
                    {projectSites.map((s) => {
                        const sBuildings = buildings.filter((b) => b.site_id === s.id);
                        return (
                            <div key={s.id} data-testid={`psp-site-${s.id}`} className="rounded-[10px] border overflow-hidden" style={{ borderColor: "#EFEAE3" }}>
                                <div className="flex items-center gap-2 px-3 py-2.5 bg-[#F6F4F0]">
                                    <MapPin className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[12.5px] font-semibold text-[#1F1F1F]">{s.name}</div>
                                        {s.address && <div className="text-[10.5px]" style={{ color: "#9A968F" }}>{s.address}</div>}
                                    </div>
                                    {canEdit && (
                                        <>
                                            <button data-testid={`psp-add-building-${s.id}`} onClick={() => setNewBldg({ siteId: s.id, name: "", building_type: "" })}
                                                className="text-[10.5px] inline-flex items-center gap-1 px-2 py-1 rounded-[6px] text-white" style={{ background: "#1F1F1F" }}>
                                                <Plus className="w-3 h-3" /> Building
                                            </button>
                                            <button data-testid={`psp-unlink-${s.id}`} onClick={() => unlink(s.id)}
                                                className="text-[10.5px] inline-flex items-center gap-1 px-2 py-1 rounded-[6px] hover:bg-[#FBE8E5]" style={{ color: "#B33A2E" }}>
                                                <Unlink className="w-3 h-3" /> Remove
                                            </button>
                                        </>
                                    )}
                                </div>
                                {newBldg && newBldg.siteId === s.id && (
                                    <div className="px-3 py-2.5 border-t flex items-center gap-2" style={{ borderColor: "#F0EDE8" }} data-testid={`psp-new-building-form-${s.id}`}>
                                        <input data-testid={`psp-new-building-name-${s.id}`} value={newBldg.name} onChange={(e) => setNewBldg((p) => ({ ...p, name: e.target.value }))}
                                            className="flex-1 px-3 py-1.5 border rounded-lg text-[12px] outline-none" style={{ borderColor: "#E5E0DA" }} placeholder="Building / address name" />
                                        <button data-testid={`psp-new-building-save-${s.id}`} onClick={createBuilding} disabled={busy}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Add
                                        </button>
                                        <button onClick={() => setNewBldg(null)} className="w-7 h-7 grid place-items-center rounded hover:bg-[#F6F4F0]"><X className="w-4 h-4" /></button>
                                    </div>
                                )}
                                {sBuildings.length === 0 ? (
                                    <div className="px-3 py-2.5 text-[11.5px] italic" style={{ color: "#9A968F" }}>No buildings/addresses under this site yet.</div>
                                ) : (
                                    <div className="divide-y" style={{ borderColor: "#F6F4F0" }}>
                                        {sBuildings.map((b) => (
                                            <div key={b.id} data-testid={`psp-building-${b.id}`} className="flex items-center gap-2 px-3 py-2">
                                                <BuildingIcon className="w-3.5 h-3.5" style={{ color: "#9A968F" }} />
                                                <span className="text-[12px] text-[#1F1F1F] flex-1">{b.name}</span>
                                                {b.building_type && <span className="text-[10.5px]" style={{ color: "#9A968F" }}>{b.building_type}</span>}
                                                {!buildingIds.includes(b.id) && (
                                                    <span className="text-[9.5px] px-1.5 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#9A968F" }}>not on project</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </SectionCard>
    );
}
